import { Sort } from '@coverright/data-access/types/medicare';
import { filterByEligible, getEffectiveDate } from '@coverright/utils';
import {
  MedigapPlanName, MedigapPlansField, MedigapQuoteQuery, MgPlansTotalDocument,
  useMedigapPlansSuspenseQuery, useMgPlansSortQuery
} from '@coverright/data-access/types/medigap';
import { useIsMAState, useIsWIState } from '@coverright/data-access/medicare';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import { useEffect, useMemo } from 'react';
import { mainBackendApolloCache } from '@coverright/data-access/apollo-clients';
import moment from 'moment';
import { addDetailsToMGPlanList, MedigapPlanOutputWithDetails } from '../medigap-plan-data';
import { planCategories, PlanCategory } from './plan-categories';

interface PlansDictionary {
  [type: string]: MedigapPlanOutputWithDetails[];
}

export function useMgPlans(quote: MedigapQuoteQuery['medigapQuote']): PlansDictionary {
  const {data: sortData} = useMgPlansSortQuery();
  const filters = quote?.medigapFilterState;
  const isMassachusetts = useIsMAState(filters?.zip);
  const isWisconsin = useIsWIState(filters?.zip);

  const effectiveDate = getEffectiveDate(filters?.effectiveDate, filters?.age);

  const planNames = useMemo(() => {
    if (isMassachusetts || isWisconsin) {
      return [];
    }

    const planCategory = filters?.planCategory || PlanCategory.MostPopular;

    if (!filters?.planNames?.length && planCategory !== 'all') {
      return (
        planCategories.find((c) => c.name === planCategory)?.plans ||
        []
      );
    }

    return filters?.planNames || [];
  }, [isWisconsin, isMassachusetts, filters]);

  const { data } = useMedigapPlansSuspenseQuery({
    variables: {
      filter: {
        ...omit(filters, ['planCategory', '__typename', 'id']),
        effectiveDate,
        companies: filters?.companies || [],
        monthlyPlanPremiumRanges: [],
        planNames,
      },
      page: {
        page: 0,
        size: 1000,
      },
      sort: [
        {
          direction: Sort.Asc,
          field: sortData?.mgPlansSort || MedigapPlansField.MonthlyPremium,
        },
      ],
    },
    skip: !filters?.gender || filters?.tobacco === undefined,
  });

  useEffect(() => {
    mainBackendApolloCache.writeQuery({
      query: MgPlansTotalDocument,
      data: {
        mgPlansTotal: data.medigapPlans.totalElements
      }
    })
  }, [data.medigapPlans]);

  const plans = data.medigapPlans.data;

  return useMemo(() => {
    let filtered = plans;

    if (!isMassachusetts && !isWisconsin) {
      filtered = plans.filter((plan) =>
        filterByEligible(
          filters?.age,
          effectiveDate
        )(plan.planName as MedigapPlanName)
      ).filter(plan => flatMap(planCategories, 'plans').includes(plan.planName));
    }

    const detailed = addDetailsToMGPlanList(filtered);

    return groupBy(detailed, 'planName');
  }, [plans, isMassachusetts, isWisconsin, filters?.age, effectiveDate]);
}
